<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { Cake, Home, Inbox, Mail, MailOpen, Mic, Paintbrush, Send, Square, Video } from 'lucide-vue-next';
import FriendHouse from '@/components/FriendHouse.vue';
import { useFamily } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';

type FriendshipCare = 'quiet' | 'warm' | 'glowing';

const {
  addPendingBirthdayMedia,
  celebratedBirthdayFriendIds,
  childFriends,
  family,
  isUnreadChildMessage,
  loadFamilyData,
  mailbox,
  messageSenderInfo,
  receivedChildMessages,
  selectFriend,
  selectedChild,
  setMailboxViewer,
  startBirthdayDrawing,
  unreadChildMessageCount
} = useFamily();
const { navigate } = useNavigation();
const birthdayCaptureFriendId = ref('');
const birthdayCaptureType = ref<'audio' | 'video' | ''>('');
const birthdayPreviewUrl = ref('');
const birthdayPreviewType = ref<'audio' | 'video' | ''>('');
const birthdayPreviewBlob = ref<Blob | null>(null);
const birthdayNotice = ref('');
const birthdayError = ref('');
const birthdayRecorder = ref<MediaRecorder | null>(null);
const birthdayVideoPreview = ref<HTMLVideoElement | null>(null);
const birthdayCaptureStream = ref<MediaStream | null>(null);
let birthdayChunks: BlobPart[] = [];

onMounted(() => {
  void loadFamilyData(family.value.id || undefined, { force: true });
});

const unreadLetters = computed(() =>
  receivedChildMessages.value.filter((message) => isUnreadChildMessage(message))
);

const mailboxSenders = computed(() =>
  unreadLetters.value.slice(0, 4).map((message) => ({
    message,
    sender: messageSenderInfo(message)
  }))
);

const birthdayFriends = computed(() => childFriends.value.filter((friend) => isBirthdayToday(friend.birthDate)));
const openBirthdayFriends = computed(() =>
  birthdayFriends.value.filter((friend) => !celebratedBirthdayFriendIds.value.includes(friend.friendChildId))
);
const celebratedBirthdayFriends = computed(() =>
  birthdayFriends.value.filter((friend) => celebratedBirthdayFriendIds.value.includes(friend.friendChildId))
);

const activeBirthdayFriend = computed(() =>
  childFriends.value.find((friend) => friend.friendChildId === birthdayCaptureFriendId.value) ?? null
);

const worldFriends = computed(() =>
  childFriends.value.map((friend) => {
    const relatedMessages = mailbox.value.filter(
      (message) =>
        (message.senderChildId === selectedChild.value.id && message.receiverChildId === friend.friendChildId) ||
        (message.receiverChildId === selectedChild.value.id && message.senderChildId === friend.friendChildId)
    );
    const approvedMessages = relatedMessages.filter((message) => message.status === 'approved' || message.status === 'received');
    const hasFlyingLetter = relatedMessages.some(
      (message) =>
        (message.status === 'received' || message.status === 'approved') &&
        message.senderChildId === friend.friendChildId &&
        message.receiverChildId === selectedChild.value.id &&
        isUnreadChildMessage(message)
    );

    const care: FriendshipCare = approvedMessages.length > 1 ? 'glowing' : relatedMessages.length > 0 ? 'warm' : 'quiet';

    return {
      friend,
      hasFlyingLetter,
      care
    };
  })
);

function openChildMailbox() {
  setMailboxViewer('child');
  navigate('mailbox');
}

function openFriend(friend: (typeof childFriends.value)[number]) {
  selectFriend(friend);
  navigate('friend');
}

function openBirthdayDrawing(friend: (typeof childFriends.value)[number]) {
  startBirthdayDrawing(friend);
  navigate('drawing');
}

async function startBirthdayCapture(friend: (typeof childFriends.value)[number], type: 'audio' | 'video') {
  birthdayNotice.value = '';
  birthdayError.value = '';
  resetBirthdayCapture();
  birthdayCaptureFriendId.value = friend.friendChildId;
  birthdayCaptureType.value = type;

  try {
    const stream = await navigator.mediaDevices.getUserMedia(
      type === 'audio' ? { audio: true } : { audio: true, video: true }
    );
    const recorder = new MediaRecorder(stream);
    birthdayChunks = [];
    birthdayRecorder.value = recorder;
    birthdayCaptureStream.value = stream;

    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        birthdayChunks.push(event.data);
      }
    });
    recorder.addEventListener('stop', () => {
      const blob = new Blob(birthdayChunks, { type: recorder.mimeType || (type === 'audio' ? 'audio/webm' : 'video/webm') });
      birthdayPreviewBlob.value = blob;
      birthdayPreviewUrl.value = URL.createObjectURL(blob);
      birthdayPreviewType.value = type;
      birthdayCaptureType.value = '';
      birthdayRecorder.value = null;
      stream.getTracks().forEach((track) => track.stop());
      birthdayCaptureStream.value = null;
    });

    if (type === 'video') {
      await nextTick();
      if (birthdayVideoPreview.value) {
        birthdayVideoPreview.value.srcObject = stream;
        await birthdayVideoPreview.value.play().catch(() => undefined);
      }
    }

    recorder.start();
  } catch {
    birthdayCaptureType.value = '';
    birthdayCaptureFriendId.value = '';
    birthdayError.value = type === 'audio' ? 'Mikrofon konnte nicht gestartet werden.' : 'Kamera konnte nicht gestartet werden.';
  }
}

function stopBirthdayCapture() {
  if (!birthdayRecorder.value || birthdayRecorder.value.state === 'inactive') return;
  birthdayRecorder.value.stop();
}

async function sendBirthdayMedia() {
  if (!activeBirthdayFriend.value || !birthdayPreviewBlob.value || !birthdayPreviewType.value) return;

  await addPendingBirthdayMedia(activeBirthdayFriend.value.friendChildId, birthdayPreviewType.value, birthdayPreviewBlob.value);
  URL.revokeObjectURL(birthdayPreviewUrl.value);
  birthdayNotice.value = 'Dein Geburtstagsgruss wartet auf die Elternfreigabe.';
  birthdayPreviewUrl.value = '';
  birthdayPreviewBlob.value = null;
  birthdayPreviewType.value = '';
  birthdayCaptureFriendId.value = '';
}

function setBirthdayVideoPreview(element: Element | ComponentPublicInstance | null) {
  birthdayVideoPreview.value = element instanceof HTMLVideoElement ? element : null;
}

function resetBirthdayCapture() {
  if (birthdayRecorder.value && birthdayRecorder.value.state !== 'inactive') {
    birthdayRecorder.value.stop();
  }
  if (birthdayPreviewUrl.value) {
    URL.revokeObjectURL(birthdayPreviewUrl.value);
  }
  if (birthdayCaptureStream.value) {
    birthdayCaptureStream.value.getTracks().forEach((track) => track.stop());
  }
  birthdayPreviewUrl.value = '';
  birthdayPreviewBlob.value = null;
  birthdayPreviewType.value = '';
  birthdayCaptureType.value = '';
  birthdayCaptureFriendId.value = '';
  birthdayCaptureStream.value = null;
}

function isBirthdayToday(birthDate?: string | null) {
  if (!birthDate) return false;
  const birthday = new Date(birthDate);
  const today = new Date();
  return birthday.getMonth() === today.getMonth() && birthday.getDate() === today.getDate();
}
</script>

<template>
  <section class="mx-auto max-w-7xl px-4 py-6 pb-28 sm:px-6 md:pb-10">
    <div class="rounded-[36px] bg-white p-6 shadow-soft md:p-8">
      <p class="text-lg font-black text-heart-leaf">{{ selectedChild.firstName }}s Freundschaftswelt</p>
      <h1 class="mt-2 text-5xl font-black text-heart-berry md:text-7xl">Freundedorf</h1>
      <p class="mt-5 max-w-3xl text-2xl font-black leading-9 text-heart-ink">
        Freundedorf – Hier wohnen Freundschaften ❤️
      </p>
    </div>

    <button
      class="focus-ring relative mt-6 flex w-full flex-col gap-5 rounded-[34px] p-5 text-left shadow-soft transition hover:-translate-y-1 active:scale-[0.99] md:flex-row md:items-center md:justify-between md:p-7"
      :class="
        unreadChildMessageCount > 0
          ? 'bg-rose-50 ring-4 ring-heart-berry/15'
          : 'bg-white'
      "
      type="button"
      aria-label="Briefkasten oeffnen"
      @click="openChildMailbox"
    >
      <span
        v-if="unreadChildMessageCount > 0"
        class="absolute right-5 top-5 grid h-12 min-w-12 place-items-center rounded-full bg-heart-berry px-4 text-2xl font-black text-white shadow-sm"
      >
        {{ unreadChildMessageCount }}
      </span>

      <span class="flex items-center gap-4">
        <span
          class="grid h-20 w-20 shrink-0 place-items-center rounded-[26px]"
          :class="unreadChildMessageCount > 0 ? 'bg-heart-berry text-white' : 'bg-slate-100 text-heart-ink'"
        >
          <Inbox class="h-11 w-11" aria-hidden="true" />
        </span>
        <span>
          <span class="block text-xl font-black text-heart-leaf">Briefkasten</span>
          <span class="mt-1 block text-3xl font-black text-heart-ink md:text-4xl">
            {{ unreadChildMessageCount > 0 ? 'Neue Briefe sind da' : 'Alles ist gelesen' }}
          </span>
        </span>
      </span>

      <span class="flex min-h-20 items-center gap-3 pr-14 md:pr-0">
        <span
          v-if="mailboxSenders.length === 0"
          class="grid h-16 w-16 place-items-center rounded-full bg-heart-mint text-heart-leaf"
          aria-hidden="true"
        >
          <MailOpen class="h-8 w-8" />
        </span>
        <span
          v-for="entry in mailboxSenders"
          :key="entry.message.id"
          class="grid h-16 w-16 place-items-center overflow-hidden rounded-full border-4 border-white text-white shadow-sm"
          :style="{ backgroundColor: entry.sender.color }"
          :title="entry.sender.name"
        >
          <img
            v-if="entry.sender.imageUrl"
            :src="entry.sender.imageUrl"
            :alt="entry.sender.name"
            class="h-full w-full object-cover"
          />
          <Home v-else class="h-8 w-8" aria-hidden="true" />
        </span>
        <Mail
          v-if="mailboxSenders.length > 0"
          class="h-10 w-10 text-heart-berry"
          aria-hidden="true"
        />
      </span>
    </button>

    <div v-if="celebratedBirthdayFriends.length > 0" class="mt-6 flex flex-wrap gap-3">
      <span
        v-for="friend in celebratedBirthdayFriends"
        :key="friend.id"
        class="inline-flex min-h-12 items-center gap-2 rounded-full bg-amber-100 px-4 text-sm font-black text-amber-800 shadow-sm"
        :aria-label="`${friend.displayName} hat heute Geburtstag`"
      >
        <Cake class="h-6 w-6" aria-hidden="true" />
        {{ friend.displayName }}
      </span>
    </div>

    <div v-if="openBirthdayFriends.length > 0" class="mt-6 grid gap-4">
      <section
        v-for="friend in openBirthdayFriends"
        :key="friend.id"
        class="rounded-[34px] bg-white p-5 shadow-soft md:p-7"
      >
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex items-center gap-4">
            <span class="grid h-20 w-20 place-items-center rounded-[26px] bg-amber-100 text-amber-800">
              <Cake class="h-11 w-11" aria-hidden="true" />
            </span>
            <div>
              <p class="text-3xl font-black text-heart-berry md:text-4xl">{{ friend.displayName }} hat heute Geburtstag 🎂</p>
              <p class="mt-2 text-lg font-black text-heart-ink">Waehle einen lieben Geburtstagsgruss.</p>
            </div>
          </div>
        </div>

        <div class="mt-5 grid gap-3 md:grid-cols-3">
          <button
            class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-rose-100 px-4 text-center text-xl font-black text-rose-800"
            type="button"
            @click="openBirthdayDrawing(friend)"
          >
            <Paintbrush class="h-10 w-10" aria-hidden="true" />
            Geburtstagsbild malen
          </button>
          <button
            v-if="birthdayCaptureType !== 'audio' || birthdayCaptureFriendId !== friend.friendChildId"
            class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-sky-100 px-4 text-center text-xl font-black text-sky-800"
            type="button"
            @click="startBirthdayCapture(friend, 'audio')"
          >
            <Mic class="h-10 w-10" aria-hidden="true" />
            Geburtstags-Sprachnachricht aufnehmen
          </button>
          <button
            v-else
            class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-sky-700 px-4 text-center text-xl font-black text-white"
            type="button"
            @click="stopBirthdayCapture"
          >
            <Square class="h-10 w-10" aria-hidden="true" />
            Aufnahme stoppen
          </button>
          <button
            v-if="birthdayCaptureType !== 'video' || birthdayCaptureFriendId !== friend.friendChildId"
            class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-heart-mint px-4 text-center text-xl font-black text-heart-leaf"
            type="button"
            @click="startBirthdayCapture(friend, 'video')"
          >
            <Video class="h-10 w-10" aria-hidden="true" />
            Geburtstagsvideo aufnehmen
          </button>
          <button
            v-else
            class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-heart-leaf px-4 text-center text-xl font-black text-white"
            type="button"
            @click="stopBirthdayCapture"
          >
            <Square class="h-10 w-10" aria-hidden="true" />
            Aufnahme stoppen
          </button>
        </div>

        <div
          v-if="birthdayCaptureType === 'video' && birthdayCaptureFriendId === friend.friendChildId"
          class="mt-5 rounded-[24px] bg-slate-900 p-3"
        >
          <video
            :ref="setBirthdayVideoPreview"
            class="max-h-[24rem] w-full rounded-2xl bg-black object-cover"
            autoplay
            muted
            playsinline
            aria-label="Videoaufnahme Vorschau"
          />
        </div>

        <div v-if="birthdayPreviewUrl && birthdayCaptureFriendId === friend.friendChildId" class="mt-5 rounded-[24px] bg-heart-cream p-4">
          <audio v-if="birthdayPreviewType === 'audio'" class="w-full" controls :src="birthdayPreviewUrl" />
          <video v-else class="max-h-[24rem] w-full rounded-2xl bg-black" controls :src="birthdayPreviewUrl" />
          <button
            class="focus-ring mt-4 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-heart-leaf px-5 text-lg font-black text-white"
            type="button"
            @click="sendBirthdayMedia"
          >
            <Send class="h-6 w-6" aria-hidden="true" />
            Geburtstagsgruss senden
          </button>
        </div>

        <p v-if="birthdayError" class="mt-4 rounded-2xl bg-rose-100 px-4 py-3 text-center text-lg font-black text-rose-800">
          {{ birthdayError }}
        </p>
        <p v-if="birthdayNotice" class="mt-4 rounded-2xl bg-heart-cream px-4 py-3 text-center text-lg font-black text-heart-leaf">
          {{ birthdayNotice }}
        </p>
      </section>
    </div>

    <div class="friendship-sky relative mt-6 overflow-hidden rounded-[38px] bg-gradient-to-b from-sky-100 via-orange-50 to-green-100 p-5 shadow-soft md:p-8">
      <div class="friendship-sun" aria-hidden="true" />
      <div class="friendship-cloud friendship-cloud-a" aria-hidden="true" />
      <div class="friendship-cloud friendship-cloud-b" aria-hidden="true" />
      <div class="friendship-meadow" aria-hidden="true" />

      <div class="relative grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <FriendHouse
          v-for="entry in worldFriends"
          :key="entry.friend.id"
          :friend="entry.friend"
          :care="entry.care"
          :has-flying-letter="entry.hasFlyingLetter"
          @click="openFriend(entry.friend)"
        />
      </div>
    </div>
  </section>
</template>
